import { Modal, OverflowTooltip, Table } from "@4uhub/lib4uhub";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IIndividualBeneficiaryData } from "../../../../models/individual-beneficiary-data";
import MainApi from "../../../../services/mainApi.service";
import BeneficiarySituationDetailsModal from "./BeneficiarySituationDetailsModal";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const BENEFICIARY_SITUATION_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/IndividualBeneficiaryData/IndividualBeneficiaryData";

const beneficiarySituationService = new MainApi<IIndividualBeneficiaryData>(
  BENEFICIARY_SITUATION_ROUTE,
);

const emptyBeneficiarySituationService = {
  getAll: async () => ({
    data: { items: [], totalCount: 0 },
  }),
} as unknown as MainApi<IIndividualBeneficiaryData>;

const translationPath =
  "page.4uSiteApp.consultationsAndLogs.beneficiarySituation.";

const formatDate = (value: unknown, language: string) => {
  if (!value) return "-";

  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "-";

  return format(date, "P", {
    locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
  }).replaceAll(",", "");
};

const BeneficiarySituation = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IIndividualBeneficiaryData | null>(null);
  const [filterParams, setFilterParams] = useState<IFilterParams>({
    Document: "",
    EstablishmentCode: "",
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const columns: GridColDef<IIndividualBeneficiaryData>[] = [
    {
      field: "personCode",
      headerName: t(translationPath + "person_code") || "Codigo da pessoa",
      flex: 0.9,
      renderCell: (params) => params.row.individualIdentifier ?? "-",
    },
    {
      field: "name",
      headerName: t(translationPath + "name") || "Nome",
      flex: 1.3,
      renderCell: (params) => {
        const text = params.row.individualName ?? "-";
        return <OverflowTooltip text={text} tooltip={text} />;
      },
    },
    {
      field: "insuredCode",
      headerName: t(translationPath + "insured_code") || "Codigo do segurado",
      flex: 0.9,
      renderCell: (params) => params.row.beneficiaryIdentifier ?? "-",
    },
    {
      field: "plan",
      headerName: t(translationPath + "plan") || "Plano",
      flex: 1.1,
      renderCell: (params) => {
        const text = params.row.planName ?? "-";
        return <OverflowTooltip text={text} tooltip={text} />;
      },
    },
    {
      field: "cardCode",
      headerName: t(translationPath + "card_code") || "Codigo da carteirinha",
      flex: 1,
      renderCell: (params) => params.row.cardCode ?? "-",
    },
    {
      field: "cardValidity",
      headerName:
        t(translationPath + "card_validity") || "Validade Carteirinha",
      flex: 1,
      renderCell: (params) =>
        formatDate(params.row.cardValidity, language),
    },
    {
      field: "contractStatus",
      headerName: t(translationPath + "contract_status") || "Situacao Contrato",
      flex: 1,
      renderCell: (params) => params.row.contractSituation ?? "-",
    },
  ];

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const closeModalHandler = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const canLoadList = useMemo(
    () => Boolean(filterParams.Document.trim()),
    [filterParams.Document],
  );

  const serviceProps = useMemo(
    () => ({
      refresh,
      Document: filterParams.Document || undefined,
      EstablishmentCode: filterParams.EstablishmentCode || undefined,
    }),
    [filterParams, refresh],
  );

  return (
    <>
      <Modal
        open={modalOpen}
        onClose={closeModalHandler}
        maxDialogWidth="lg"
        title={t(translationPath + "details") || "Dados completos"}
      >
        {selectedRow && <BeneficiarySituationDetailsModal data={selectedRow} />}
      </Modal>
      <Table<IIndividualBeneficiaryData>
        title={t(translationPath + "title") || "Situacao do Beneficiario"}
        columns={columns}
        service={
          canLoadList
            ? beneficiarySituationService
            : emptyBeneficiarySituationService
        }
        serviceProps={canLoadList ? serviceProps : undefined}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Pesquisar..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions
        defaultPageSize={100}
        getRowId={(row) =>
          `${row.beneficiaryIdentifier ?? ""}-${row.individualIdentifier ?? ""}-${
            row.cardCode ?? ""
          }`
        }
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
        ]}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default BeneficiarySituation;
